import * as React from 'react'
import styled from 'styled-components'
import { useHydrateAtoms } from 'jotai/utils'

import * as plume from '@ulule/owl-kit-components/next'

import { listingLayout } from '@boutique/components'
import * as service from '@boutique/services'
import * as cold from '@owl-nest/cold-data/client'

import { useProductListing } from './hooks/useProductListing'
import { Breadcrumbs } from './Breadcrumbs'
import { Filters } from './Filters'
import { Heading } from './Heading'
import { Pagination } from './Pagination'
import { RelativeControls } from './RelativeControls'
import { Results } from './Results'
import { Seo } from './Seo'
import { getSEOTexts } from './seoUtils'
import { Hierarchy } from './Hierarchy'

export type ProductListProps = {
  productCache: service.search.product.Cache
}

export function ProductList({ productCache }: ProductListProps): React.ReactElement {
  useHydrateAtoms([[service.search.product.PRODUCT_CACHE, productCache]])
  return <Page />
}

function Page(): React.ReactElement {
  const { query } = useProductListing()

  const selectedCategory = CATEGORIES.find((c) => c.slug === query.facetFilters?.category)

  const seoTexts = getSEOTexts(
    query.facetFilters?.genders,
    query.facetFilters?.types,
    query.facetFilters?.brands,
    query.facetFilters?.environmentalPillars,
    query.facetFilters?.socialPillars,
    query.hierarchy,
    selectedCategory,
  )

  return (
    <Wrapper>
      <Seo htmlTitle={seoTexts.htmlTitle} seoText={seoTexts.h2} />
      <Breadcrumbs />
      <Heading seoH1Text={seoTexts.h1} seoH2Text={seoTexts.h2} />
      <RelativeControls />
      <listingLayout.Wrapper>
        <listingLayout.ScrollAnchor id="top" />
        <listingLayout.Aside>
          {!query.fullTextSearch && !query.searchableTag && (
            <Hierarchy activeId={query.hierarchy ?? query.facetFilters?.category} />
          )}

          <Filters />
        </listingLayout.Aside>
        <listingLayout.Main>
          <Results />
          <Pagination />
        </listingLayout.Main>
      </listingLayout.Wrapper>
    </Wrapper>
  )
}

const CATEGORIES = cold.types['category'].flatMap((slug) =>
  cold.taxonomies[slug].children.tags.map((tagSlug) => {
    const tag = cold.tags[tagSlug]
    return {
      name: tag.name,
      slug: tag.slug,
      descriptionShort: tag.category!.descriptionShort,
    }
  }),
)

const Wrapper = styled(plume.Container)`
  ${plume.Breadcrumbs} {
    margin-bottom: 32px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      margin-bottom: 24px;
    }
  }
`
